import {
  ComponentType,
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
} from 'react';

import { useModal } from 'mui-modal-provider';

import {
  Button,
  ButtonProps,
  Dialog,
  HexIcon,
  HexIconElementProps,
  ShowModalOutput,
  Placeholder as StatusMessage,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

type Variant = 'error' | 'check' | 'notification';

type Props = {
  title: string;
  name?: string;
  body: ReactNode;
  icon: ReactElement<HexIconElementProps>;
  highlightBody?: boolean;
  highlightBodyVariant?: 'predictive' | 'default';
  variant?: Variant;
  cancelBtnLabel?: string;
  confirmBtnLabel?: string;
  confirmButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  hideCorfirmButton?: boolean;
  loading?: boolean;
  testId?: string;
  onCancel?: () => void;
};

type ConfirmationDialogProps = Props & {
  close: () => void;
};

type ConfirmationDialogContentProps = Pick<
  Props,
  | 'name'
  | 'body'
  | 'title'
  | 'variant'
  | 'icon'
  | 'highlightBody'
  | 'highlightBodyVariant'
> & {
  actions: ReactNode;
};

const getHighlightBodyBgColor = (
  variant: Variant,
  highlightBodyVariant: Props['highlightBodyVariant']
): string => {
  if (highlightBodyVariant === 'predictive') {
    return 'indigo.50';
  }
  switch (variant) {
    case 'error':
      return 'red.50';
    case 'check':
      return 'blue.100';
    case 'notification':
    default:
      return 'grey.100';
  }
};

const PolicyConfirmationDialogContent = ({
  name,
  actions,
  body,
  title,
  icon,
  variant = 'notification',
  highlightBody = false,
  highlightBodyVariant = 'default',
}: ConfirmationDialogContentProps) => {
  return (
    <StatusMessage
      sx={{ position: 'relative', top: -42 }}
      testId="content"
      icon={<HexIcon variant={variant} icon={icon} size={84} />}
      titleProps={{ component: 'div', variant: 'h5', maxWidth: 480 }}
      title={
        <>
          {name && (
            <Typography
              variant="A1"
              color={tokens.theme.fg.structural.secondary}
              pt="8px"
            >
              {name}
            </Typography>
          )}
          <Typography variant="h5">{title}</Typography>
        </>
      }
      body={
        <Box component="span" maxWidth={430}>
          {body}
        </Box>
      }
      bodyProps={{
        variant: 'P14R',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mt: 12,
        mb: 2,
        ...(highlightBody && {
          bgcolor: getHighlightBodyBgColor(variant, highlightBodyVariant),
          color: 'grey.600',
          py: 16,
        }),
      }}
      actions={
        <Stack direction="row" gap={16}>
          {actions}
        </Stack>
      }
    />
  );
};

export const ConfirmationDialog = ({
  close,
  testId = 'confirmation-dialog',
  title,
  body,
  name,
  variant = 'notification',
  cancelBtnLabel = 'Cancel',
  confirmBtnLabel = 'Conrfirm',
  icon,
  highlightBody,
  highlightBodyVariant,
  confirmButtonProps,
  cancelButtonProps,
  hideCorfirmButton = false,
  loading = false,
}: ConfirmationDialogProps) => {
  const confirmationButtonVariant =
    variant === 'error'
      ? 'danger'
      : variant === 'check'
      ? 'primary'
      : variant === 'notification'
      ? 'primary'
      : 'primary';

  return (
    <Dialog open PaperProps={{ sx: { overflow: 'visible' } }} testId={testId}>
      <PolicyConfirmationDialogContent
        name={name}
        icon={icon}
        title={title}
        body={body}
        variant={variant}
        highlightBody={highlightBody}
        highlightBodyVariant={highlightBodyVariant}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={close}
              testId="cancel"
              disabled={loading}
              {...cancelButtonProps}
            >
              {cancelBtnLabel}
            </Button>
            {!hideCorfirmButton && (
              <Button
                variant={confirmationButtonVariant}
                onClick={close}
                testId="confirm"
                loading={loading}
                {...confirmButtonProps}
              >
                {confirmBtnLabel}
              </Button>
            )}
          </>
        }
      />
    </Dialog>
  );
};

export const useConfirmationDialog = () => {
  const { showModal } = useModal();

  const modalInstanceRef = useRef<ShowModalOutput | null>(null);
  return {
    openConfirmationDialog: (props: Props) => {
      modalInstanceRef.current = showModal(ConfirmationDialog, {
        close: () => {
          modalInstanceRef.current?.destroy();
          props.onCancel?.();
        },
        ...props,
      });
    },
    closeConfirmationDialog: () => {
      modalInstanceRef.current?.destroy();
    },
  };
};

export type GenericConfirmationDialogProps<T> = { close: () => void } & T;

export const useConfirmationDialogGeneric = <T,>(
  Comp: ComponentType<GenericConfirmationDialogProps<T>>
) => {
  const { showModal } = useModal();

  const modalInstanceRef = useRef<ShowModalOutput | null>(null);

  return {
    openConfirmationDialog: useCallback(
      (props: T) => {
        modalInstanceRef.current = showModal(Comp, {
          close: () => modalInstanceRef.current?.destroy(),
          ...props,
        }) as unknown as ShowModalOutput;
      },
      [Comp, showModal]
    ),
  };
};
